<template>
  <div class="security-geofences">
    <v-container grid-list-md>
      <v-layout row wrap>
        <v-flex xs12>
          <h1>{{ $t('security_geofences') }}</h1>
          <p>{{ $t('security_geofences_description') }}</p>
        </v-flex>
      </v-layout>

      <v-alert v-if="securityError" type="error" dismissible class="mb-3">
        {{ securityError }}
      </v-alert>

      <v-layout row wrap>
        <!-- MAPA -->
        <v-flex xs12 md8>
          <v-card class="elevation-3">
            <v-toolbar dense color="primary" dark>
              <v-toolbar-title>{{ $t('security_geofences') }}</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn icon @click="locateMe" :title="$t('security_geofences_locate_me')">
                <v-icon>my_location</v-icon>
              </v-btn>
              <v-btn
                v-if="focusedGeocercaId && !isDrawing && !isAddingCheckpoint"
                color="white"
                class="primary--text"
                small
                @click="startAddingCheckpoint"
              >
                <v-icon left small>add_location_alt</v-icon>
                {{ $t('security_checkpoints_add') }}
              </v-btn>
              <v-btn
                v-if="!isDrawing && !isAddingCheckpoint"
                color="white"
                class="primary--text"
                small
                @click="startDrawing"
              >
                <v-icon left small>add_location</v-icon>
                {{ $t('security_geofences_new') }}
              </v-btn>
            </v-toolbar>

            <!-- BARRA DE AGREGAR CHECKPOINT -->
            <v-card-text v-if="isAddingCheckpoint" class="pa-2 drawing-toolbar">
              <v-layout row wrap align-center>
                <v-flex xs12 sm8>
                  <v-icon small color="primary" class="mr-1">info</v-icon>
                  <span class="caption">{{ $t('security_checkpoints_hint') }}</span>
                </v-flex>
                <v-flex xs12 sm4 class="text-xs-right">
                  <v-btn small flat color="error" @click="cancelAddingCheckpoint">
                    {{ $t('security_geofences_cancel_drawing') }}
                  </v-btn>
                </v-flex>
              </v-layout>
            </v-card-text>

            <!-- BARRA DE DIBUJO -->
            <v-card-text v-if="isDrawing" class="pa-2 drawing-toolbar">
              <v-layout row wrap align-center>
                <v-flex xs12 sm5>
                  <v-icon small color="primary" class="mr-1">info</v-icon>
                  <span class="caption">{{ $t('security_geofences_drawing_hint') }}</span>
                </v-flex>
                <v-flex xs12 sm2 class="text-xs-center">
                  <v-chip small color="primary" text-color="white">
                    {{ $t('security_geofences_points_count', { count: drawPoints.length }) }}
                  </v-chip>
                </v-flex>
                <v-flex xs12 sm5 class="text-xs-right">
                  <v-btn small flat @click="undoLastPoint" :disabled="drawPoints.length === 0">
                    <v-icon left small>undo</v-icon>
                    {{ $t('security_geofences_undo_point') }}
                  </v-btn>
                  <v-btn small flat color="error" @click="cancelDrawing">
                    {{ $t('security_geofences_cancel_drawing') }}
                  </v-btn>
                  <v-btn
                    small
                    color="success"
                    :disabled="drawPoints.length < 3"
                    @click="finishDrawing"
                  >
                    <v-icon left small>check</v-icon>
                    {{ $t('security_geofences_finish_drawing') }}
                  </v-btn>
                </v-flex>
              </v-layout>
              <v-alert v-if="drawPoints.length > 0 && drawPoints.length < 3" type="info" :value="true" dense class="mt-2 mb-0 caption">
                {{ $t('security_geofences_min_points') }}
              </v-alert>
            </v-card-text>

            <div id="geofences-map" class="geofences-map"></div>
          </v-card>
        </v-flex>

        <!-- LISTA DE GEOCERCAS -->
        <v-flex xs12 md4>
          <v-card class="elevation-3">
            <v-toolbar dense color="blue-grey darken-1" dark>
              <v-toolbar-title>{{ geocercas.length }}</v-toolbar-title>
            </v-toolbar>
            <v-progress-linear v-if="isLoading" indeterminate class="mb-0"></v-progress-linear>

            <v-list v-if="geocercas.length > 0" two-line>
              <template v-for="(geocerca, index) in geocercas">
                <v-list-tile
                  :key="geocerca.id"
                  :class="{ 'grey lighten-4': geocerca.id === focusedGeocercaId }"
                  @click="selectGeocerca(geocerca)"
                >
                  <v-list-tile-avatar>
                    <v-icon :color="geocerca.is_active ? 'success' : 'grey'">
                      {{ geocerca.is_active ? 'check_circle' : 'block' }}
                    </v-icon>
                  </v-list-tile-avatar>
                  <v-list-tile-content>
                    <v-list-tile-title>{{ geocerca.nombre }}</v-list-tile-title>
                    <v-list-tile-sub-title>{{ geocerca.descripcion || '-' }}</v-list-tile-sub-title>
                  </v-list-tile-content>
                  <v-list-tile-action>
                    <v-menu bottom left @click.stop>
                      <template v-slot:activator="{ on }">
                        <v-btn icon v-on="on">
                          <v-icon>more_vert</v-icon>
                        </v-btn>
                      </template>
                      <v-list>
                        <v-list-tile @click="toggleActive(geocerca)">
                          <v-list-tile-title>
                            {{ geocerca.is_active ? $t('security_geofences_inactive') : $t('security_geofences_active') }}
                          </v-list-tile-title>
                        </v-list-tile>
                        <v-list-tile @click="openDeleteDialog(geocerca)">
                          <v-list-tile-title class="error--text">{{ $t('eliminar') || 'Eliminar' }}</v-list-tile-title>
                        </v-list-tile>
                      </v-list>
                    </v-menu>
                  </v-list-tile-action>
                </v-list-tile>
                <v-divider v-if="index < geocercas.length - 1" :key="`div-${geocerca.id}`"></v-divider>
              </template>
            </v-list>

            <v-card-text v-else-if="!isLoading">
              <v-alert type="info" :value="true">
                {{ $t('security_geofences_empty') }}
              </v-alert>
            </v-card-text>
          </v-card>

          <!-- CHECKPOINTS DE LA GEOCERCA SELECCIONADA -->
          <v-card v-if="focusedGeocercaId" class="elevation-3 mt-3">
            <v-toolbar dense color="blue-grey darken-1" dark>
              <v-toolbar-title>{{ $t('security_checkpoints_title') }}</v-toolbar-title>
            </v-toolbar>
            <v-progress-linear v-if="isLoadingCheckpoints" indeterminate class="mb-0"></v-progress-linear>

            <v-list v-if="focusedCheckpoints.length > 0" dense two-line>
              <template v-for="(checkpoint, index) in focusedCheckpoints">
                <v-list-tile :key="checkpoint.id">
                  <v-list-tile-avatar>
                    <v-chip small color="primary" text-color="white">{{ checkpoint.orden }}</v-chip>
                  </v-list-tile-avatar>
                  <v-list-tile-content>
                    <v-list-tile-title>{{ checkpoint.nombre }}</v-list-tile-title>
                    <v-list-tile-sub-title>{{ $t('security_checkpoints_radius_label') }}: {{ checkpoint.radio_metros }}m</v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>
                <v-divider v-if="index < focusedCheckpoints.length - 1" :key="`cdiv-${checkpoint.id}`"></v-divider>
              </template>
            </v-list>

            <v-card-text v-else-if="!isLoadingCheckpoints">
              <v-alert type="info" :value="true" dense>
                {{ $t('security_checkpoints_empty') }}
              </v-alert>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>

    <!-- DIALOGO CREAR GEOCERCA -->
    <v-dialog v-model="showCreateDialog" max-width="450px" persistent>
      <v-card>
        <v-card-title>
          <span class="title">{{ $t('security_geofences_new') }}</span>
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="form.nombre"
            :label="$t('security_geofences_name')"
            maxlength="100"
            counter
            :error-messages="nombreErrors"
          ></v-text-field>
          <v-textarea
            v-model="form.descripcion"
            :label="$t('security_geofences_description_field')"
            rows="2"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="cancelCreateDialog">{{ $t('cancelar') || 'Cancelar' }}</v-btn>
          <v-btn color="primary" :loading="isLoading" @click="saveGeocerca">
            {{ $t('security_geofences_save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DIALOGO ELIMINAR GEOCERCA -->
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title>{{ $t('security_geofences_delete_confirm_title') }}</v-card-title>
        <v-card-text v-if="selectedGeocerca">
          {{ $t('security_geofences_delete_confirm_msg') }}
          <strong>{{ selectedGeocerca.nombre }}</strong>?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="showDeleteDialog = false">{{ $t('cancelar') || 'Cancelar' }}</v-btn>
          <v-btn color="error" flat :loading="isLoading" @click="deleteGeocerca">
            {{ $t('eliminar') || 'Eliminar' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DIALOGO CREAR CHECKPOINT -->
    <v-dialog v-model="showCheckpointDialog" max-width="450px" persistent>
      <v-card>
        <v-card-title>
          <span class="title">{{ $t('security_checkpoints_add') }}</span>
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="checkpointForm.nombre"
            :label="$t('security_checkpoints_name')"
            maxlength="100"
            counter
            :error-messages="checkpointNombreErrors"
          ></v-text-field>
          <v-textarea
            v-model="checkpointForm.descripcion"
            :label="$t('security_geofences_description_field')"
            rows="2"
          ></v-textarea>
          <v-text-field
            v-model.number="checkpointForm.radio_metros"
            type="number"
            min="10"
            :label="$t('security_checkpoints_radius')"
            :hint="$t('security_checkpoints_radius_hint')"
            persistent-hint
            :error-messages="checkpointRadioErrors"
          ></v-text-field>
          <v-text-field
            v-model.number="checkpointForm.orden"
            type="number"
            min="1"
            :label="$t('security_checkpoints_order')"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="cancelCheckpointDialog">{{ $t('cancelar') || 'Cancelar' }}</v-btn>
          <v-btn color="primary" :loading="isLoading" @click="saveCheckpoint">
            {{ $t('security_checkpoints_save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import {
  FETCH_GEOCERCAS,
  CREATE_GEOCERCA,
  UPDATE_GEOCERCA,
  DELETE_GEOCERCA,
  FETCH_GEOCERCA_DETAIL,
  CREATE_CHECKPOINT
} from '../../../store/modules/security'
import { USER_REQUEST } from '../../../store/actions/user'
import { mapGetters } from 'vuex'

const DEFAULT_CENTER = [22.1565, -100.9855] // San Luis Potosí, México
const DEFAULT_ZOOM = 13
const ACTIVE_COLOR = '#2e7d32'
const INACTIVE_COLOR = '#9e9e9e'
const DRAWING_COLOR = '#1976d2'
const CHECKPOINT_COLOR = '#f57c00'

export default {
  name: 'SecurityGeofences',
  data() {
    return {
      map: null,
      geocercaLayers: {},
      isDrawing: false,
      drawPoints: [],
      drawPreviewLayer: null,
      drawMarkersLayer: null,
      showCreateDialog: false,
      showDeleteDialog: false,
      selectedGeocerca: null,
      formSubmitted: false,
      form: {
        nombre: '',
        descripcion: ''
      },
      focusedGeocercaId: null,
      isLoadingCheckpoints: false,
      checkpointsLayerGroup: null,
      isAddingCheckpoint: false,
      showCheckpointDialog: false,
      checkpointFormSubmitted: false,
      checkpointForm: {
        nombre: '',
        descripcion: '',
        radio_metros: 50,
        orden: 1,
        lat: null,
        lng: null
      }
    }
  },
  computed: {
    ...mapGetters(['getToken', 'getProfile']),
    ...mapGetters('security', ['isSecurityLoading', 'getSecurityError', 'getGeocercas', 'getGeocercaDetail']),
    isLoading() {
      return this.isSecurityLoading
    },
    securityError() {
      return this.getSecurityError
    },
    geocercas() {
      return this.getGeocercas
    },
    isParkAdmin() {
      return this.getProfile && this.getProfile.roll === 4
    },
    isCompanyAdminWithSecurity() {
      return this.getProfile && this.getProfile.roll === 5 && this.getProfile.enable_security === true
    },
    hasAccess() {
      return this.isParkAdmin || this.isCompanyAdminWithSecurity
    },
    nombreErrors() {
      if (!this.formSubmitted) return []
      return this.form.nombre.trim() ? [] : [this.$t('security_geofences_name_required')]
    },
    focusedCheckpoints() {
      const detail = this.getGeocercaDetail
      if (!detail || detail.id !== this.focusedGeocercaId || !Array.isArray(detail.checkpoints)) return []
      return [...detail.checkpoints].sort((a, b) => a.orden - b.orden)
    },
    checkpointNombreErrors() {
      if (!this.checkpointFormSubmitted) return []
      return this.checkpointForm.nombre.trim() ? [] : [this.$t('security_checkpoints_name_required')]
    },
    checkpointRadioErrors() {
      if (!this.checkpointFormSubmitted) return []
      const radio = Number(this.checkpointForm.radio_metros)
      return (radio && radio >= 10) ? [] : [this.$t('security_checkpoints_radius_error')]
    }
  },
  methods: {
    fetchGeocercas() {
      this.$store.dispatch(`security/${FETCH_GEOCERCAS}`, this.getToken)
        .then(geocercas => {
          this.renderGeocercas(geocercas)
          this.centerMap(geocercas)
        })
        .catch(() => {})
    },
    centerMap(geocercas) {
      const layers = Object.values(this.geocercaLayers)
      if (geocercas && geocercas.length > 0 && layers.length > 0) {
        const group = L.featureGroup(layers)
        this.map.fitBounds(group.getBounds(), { maxZoom: 17, padding: [20, 20] })
        return
      }
      // Sin geocercas todavía: ubicar al usuario para facilitar el dibujo del perímetro
      this.locateMe(true)
    },
    initMap() {
      this.map = L.map('geofences-map').setView(DEFAULT_CENTER, DEFAULT_ZOOM)
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
        maxZoom: 19
      }).addTo(this.map)

      this.map.on('click', this.onMapClick)

      this.$nextTick(() => {
        this.map.invalidateSize()
      })
      window.addEventListener('resize', this.handleResize)
    },
    handleResize() {
      if (this.map) this.map.invalidateSize()
    },
    parseCoordenadas(geocerca) {
      if (Array.isArray(geocerca.coordenadas)) return geocerca.coordenadas
      try {
        return JSON.parse(geocerca.coordenadas) || []
      } catch (e) {
        return []
      }
    },
    renderGeocercas(geocercas) {
      Object.values(this.geocercaLayers).forEach(layer => this.map.removeLayer(layer))
      this.geocercaLayers = {}

      geocercas.forEach(geocerca => {
        const points = this.parseCoordenadas(geocerca)
        if (points.length < 3) return

        const latlngs = points.map(p => [p.lat, p.lng])
        const color = geocerca.is_active ? ACTIVE_COLOR : INACTIVE_COLOR
        const polygon = L.polygon(latlngs, {
          color: color,
          weight: 2,
          fillColor: color,
          fillOpacity: 0.15
        }).addTo(this.map)
        polygon.bindTooltip(geocerca.nombre)
        this.geocercaLayers[geocerca.id] = polygon
      })
    },
    focusGeocerca(geocerca) {
      const layer = this.geocercaLayers[geocerca.id]
      if (layer && this.map) {
        this.map.fitBounds(layer.getBounds(), { maxZoom: 17 })
        layer.openTooltip()
      }
    },
    selectGeocerca(geocerca) {
      this.focusedGeocercaId = geocerca.id
      this.focusGeocerca(geocerca)
      this.fetchGeocercaDetail(geocerca.id)
    },
    fetchGeocercaDetail(geocercaId) {
      this.isLoadingCheckpoints = true
      this.$store.dispatch(`security/${FETCH_GEOCERCA_DETAIL}`, { token: this.getToken, geocercaId })
        .then(detail => {
          this.renderCheckpoints(detail)
        })
        .catch(() => {})
        .finally(() => {
          this.isLoadingCheckpoints = false
        })
    },
    clearCheckpointLayers() {
      if (this.checkpointsLayerGroup) {
        this.map.removeLayer(this.checkpointsLayerGroup)
        this.checkpointsLayerGroup = null
      }
    },
    renderCheckpoints(detail) {
      this.clearCheckpointLayers()
      if (!detail || !Array.isArray(detail.checkpoints) || detail.checkpoints.length === 0) return

      const layers = detail.checkpoints.map(checkpoint => {
        const lat = parseFloat(checkpoint.latitude)
        const lng = parseFloat(checkpoint.longitude)
        const marker = L.circleMarker([lat, lng], {
          radius: 7,
          color: '#000',
          weight: 1,
          fillColor: CHECKPOINT_COLOR,
          fillOpacity: 0.9
        }).bindTooltip(`${checkpoint.orden}. ${checkpoint.nombre}`)
        const coverage = L.circle([lat, lng], {
          radius: checkpoint.radio_metros,
          color: CHECKPOINT_COLOR,
          weight: 1,
          dashArray: '4, 4',
          fillColor: CHECKPOINT_COLOR,
          fillOpacity: 0.08
        })
        return L.layerGroup([coverage, marker])
      })

      this.checkpointsLayerGroup = L.layerGroup(layers).addTo(this.map)
    },
    startAddingCheckpoint() {
      this.cancelDrawing()
      this.isAddingCheckpoint = true
    },
    cancelAddingCheckpoint() {
      this.isAddingCheckpoint = false
    },
    openCheckpointDialog(lat, lng) {
      this.checkpointForm = {
        nombre: '',
        descripcion: '',
        radio_metros: 50,
        orden: this.focusedCheckpoints.length + 1,
        lat,
        lng
      }
      this.checkpointFormSubmitted = false
      this.showCheckpointDialog = true
    },
    cancelCheckpointDialog() {
      this.showCheckpointDialog = false
    },
    saveCheckpoint() {
      this.checkpointFormSubmitted = true
      const radio = Number(this.checkpointForm.radio_metros)
      if (!this.checkpointForm.nombre.trim() || !radio || radio < 10) return

      this.$store.dispatch(`security/${CREATE_CHECKPOINT}`, {
        token: this.getToken,
        nombre: this.checkpointForm.nombre.trim(),
        descripcion: this.checkpointForm.descripcion,
        latitude: this.checkpointForm.lat.toFixed(6),
        longitude: this.checkpointForm.lng.toFixed(6),
        radioMetros: radio,
        geocercaId: this.focusedGeocercaId,
        orden: Number(this.checkpointForm.orden) || (this.focusedCheckpoints.length + 1)
      })
        .then(() => {
          this.$store.commit('snackbar_change_message', this.$t('security_checkpoints_created_success'))
          this.$store.commit('snackbar_change_color', 'success')
          this.$store.commit('snackbar_activate_true')
          this.showCheckpointDialog = false
          this.fetchGeocercaDetail(this.focusedGeocercaId)
        })
        .catch(() => {
          this.$store.commit('snackbar_change_message', this.securityError || this.$t('security_checkpoints_error_creating'))
          this.$store.commit('snackbar_change_color', 'error')
          this.$store.commit('snackbar_activate_true')
        })
    },
    onMapClick(e) {
      if (this.isDrawing) {
        this.drawPoints.push({ lat: e.latlng.lat, lng: e.latlng.lng })
        this.updateDrawPreview()
        return
      }
      if (this.isAddingCheckpoint) {
        this.isAddingCheckpoint = false
        this.openCheckpointDialog(e.latlng.lat, e.latlng.lng)
      }
    },
    updateDrawPreview() {
      if (this.drawPreviewLayer) {
        this.map.removeLayer(this.drawPreviewLayer)
        this.drawPreviewLayer = null
      }
      if (this.drawMarkersLayer) {
        this.map.removeLayer(this.drawMarkersLayer)
        this.drawMarkersLayer = null
      }
      if (this.drawPoints.length === 0) return

      const latlngs = this.drawPoints.map(p => [p.lat, p.lng])
      this.drawMarkersLayer = L.layerGroup(
        this.drawPoints.map(p =>
          L.circleMarker([p.lat, p.lng], {
            radius: 5,
            color: '#000',
            weight: 1,
            fillColor: DRAWING_COLOR,
            fillOpacity: 0.9
          })
        )
      ).addTo(this.map)

      if (this.drawPoints.length >= 2) {
        this.drawPreviewLayer = L.polygon(latlngs, {
          color: DRAWING_COLOR,
          weight: 2,
          dashArray: '6, 6',
          fillColor: DRAWING_COLOR,
          fillOpacity: 0.1
        }).addTo(this.map)
      }
    },
    startDrawing() {
      this.cancelAddingCheckpoint()
      this.isDrawing = true
      this.drawPoints = []
      this.updateDrawPreview()
    },
    undoLastPoint() {
      this.drawPoints.pop()
      this.updateDrawPreview()
    },
    cancelDrawing() {
      this.isDrawing = false
      this.drawPoints = []
      this.updateDrawPreview()
    },
    finishDrawing() {
      if (this.drawPoints.length < 3) return
      this.form.nombre = ''
      this.form.descripcion = ''
      this.formSubmitted = false
      this.showCreateDialog = true
    },
    cancelCreateDialog() {
      this.showCreateDialog = false
    },
    saveGeocerca() {
      this.formSubmitted = true
      if (!this.form.nombre.trim()) return

      const points = [...this.drawPoints]
      const first = points[0]
      const last = points[points.length - 1]
      if (first.lat !== last.lat || first.lng !== last.lng) {
        points.push({ lat: first.lat, lng: first.lng })
      }

      this.$store.dispatch(`security/${CREATE_GEOCERCA}`, {
        token: this.getToken,
        nombre: this.form.nombre.trim(),
        coordenadas: points,
        descripcion: this.form.descripcion
      })
        .then(() => {
          this.$store.commit('snackbar_change_message', this.$t('security_geofences_created_success'))
          this.$store.commit('snackbar_change_color', 'success')
          this.$store.commit('snackbar_activate_true')
          this.showCreateDialog = false
          this.cancelDrawing()
          this.fetchGeocercas()
        })
        .catch(() => {
          this.$store.commit('snackbar_change_message', this.securityError || this.$t('security_geofences_error_creating'))
          this.$store.commit('snackbar_change_color', 'error')
          this.$store.commit('snackbar_activate_true')
        })
    },
    toggleActive(geocerca) {
      this.$store.dispatch(`security/${UPDATE_GEOCERCA}`, {
        token: this.getToken,
        geocercaId: geocerca.id,
        isActive: !geocerca.is_active
      })
        .then(() => {
          this.$store.commit('snackbar_change_message', this.$t('security_geofences_toggle_active_success'))
          this.$store.commit('snackbar_change_color', 'success')
          this.$store.commit('snackbar_activate_true')
          this.fetchGeocercas()
        })
        .catch(() => {
          this.$store.commit('snackbar_change_message', this.securityError || this.$t('security_geofences_error_updating'))
          this.$store.commit('snackbar_change_color', 'error')
          this.$store.commit('snackbar_activate_true')
        })
    },
    openDeleteDialog(geocerca) {
      this.selectedGeocerca = geocerca
      this.showDeleteDialog = true
    },
    deleteGeocerca() {
      const deletedId = this.selectedGeocerca.id
      this.$store.dispatch(`security/${DELETE_GEOCERCA}`, {
        token: this.getToken,
        geocercaId: deletedId
      })
        .then(() => {
          this.$store.commit('snackbar_change_message', this.$t('security_geofences_deleted_success'))
          this.$store.commit('snackbar_change_color', 'success')
          this.$store.commit('snackbar_activate_true')
          this.showDeleteDialog = false
          if (deletedId === this.focusedGeocercaId) {
            this.focusedGeocercaId = null
            this.clearCheckpointLayers()
          }
          this.fetchGeocercas()
        })
        .catch(() => {
          this.$store.commit('snackbar_change_message', this.securityError || this.$t('security_geofences_error_deleting'))
          this.$store.commit('snackbar_change_color', 'error')
          this.$store.commit('snackbar_activate_true')
        })
    },
    locateMe(isInitialCenter) {
      if (!navigator.geolocation || !this.map) {
        if (isInitialCenter && this.map) this.map.setView(DEFAULT_CENTER, DEFAULT_ZOOM)
        return
      }
      navigator.geolocation.getCurrentPosition(
        position => {
          this.map.setView([position.coords.latitude, position.coords.longitude], 16)
        },
        () => {
          if (isInitialCenter) this.map.setView(DEFAULT_CENTER, DEFAULT_ZOOM)
        }
      )
    },
    validateAndInit() {
      if (!this.hasAccess) {
        this.$router.push('/')
        return
      }
      this.$nextTick(() => {
        this.initMap()
        this.fetchGeocercas()
      })
    }
  },
  mounted() {
    if (!this.$store.getters.isAuthenticated) {
      this.$router.push('/')
      return
    }

    if (!this.$store.getters.isProfileLoaded) {
      this.$store.dispatch(USER_REQUEST)
        .then(() => {
          this.validateAndInit()
        })
        .catch(() => {
          this.$router.push('/')
        })
    } else {
      this.validateAndInit()
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }
}
</script>

<style scoped>
.geofences-map {
  width: 100%;
  height: 520px;
  /* Aísla el mapa en su propio stacking context: los panes internos de Leaflet
     usan z-index hasta 700 y, sin esto, se dibujan por encima del v-dialog. */
  position: relative;
  z-index: 0;
}
.drawing-toolbar {
  background: #e3f2fd;
  border-bottom: 1px solid #bbdefb;
}
.mb-3 {
  margin-bottom: 20px;
}
</style>
